import { Mail, MessageCircle, Phone } from "lucide-react";
import type { Identifier } from "ra-core";
import { useTranslate } from "ra-core";
import { useContext } from "react";

import { Button } from "@/components/ui/button";

import { TeamContactsContext } from "./TeamContactsContext";
import { useTeamContacts } from "./useTeamContacts";

export interface TeamContactActionsProps {
  salesId: Identifier;
  /** The person's CRM email, for the `mailto:` compose link. */
  email?: string | null;
}

/** Digits only: `wa.me` wants the E.164 number without '+' or separators. */
const toDigits = (phone: string) => phone.replace(/\D/g, "");

/**
 * Almikia Epic EC2c (TASK-133): three 44 px quick-contact links for a team
 * member -- WhatsApp chat (`wa.me`), plain `tel:` call and `mailto:`. There is
 * deliberately NO WhatsApp-call action. Renders nothing for a logged-out or
 * role-less identity; a button shows only when its number/email exists.
 */
export const TeamContactActions = ({
  salesId,
  email,
}: TeamContactActionsProps) => {
  const translate = useTranslate();
  // Inside a `TeamContactsProvider` the page-wide batch supplies the phones,
  // so this row asks for none of its own (empty ids disable the query).
  const batchedPhones = useContext(TeamContactsContext);
  const own = useTeamContacts(batchedPhones ? [] : [salesId]);
  if (!own.isAuthenticatedMember) return null;

  const phones = batchedPhones ?? own.phones;
  const digits = phones[String(salesId)]
    ? toDigits(phones[String(salesId)])
    : "";
  if (!digits && !email) return null;

  return (
    // stopPropagation keeps a click here from triggering a parent row click.
    <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
      {digits ? (
        <>
          <Button asChild variant="outline" className="min-h-11">
            <a
              href={`https://wa.me/${digits}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle aria-hidden="true" />
              {translate("crm.team_contact.whatsapp")}
            </a>
          </Button>
          <Button asChild variant="outline" className="min-h-11">
            <a href={`tel:+${digits}`}>
              <Phone aria-hidden="true" />
              {translate("crm.team_contact.call")}
            </a>
          </Button>
        </>
      ) : null}
      {email ? (
        <Button asChild variant="outline" className="min-h-11">
          <a href={`mailto:${email}`}>
            <Mail aria-hidden="true" />
            {translate("crm.team_contact.email")}
          </a>
        </Button>
      ) : null}
    </div>
  );
};
